/**
 * FileVault Angular Models
 * Matches the C# models from FileVault.Core
 */

export interface FileVaultItem {
  id: string;
  legacyId?: number;
  fileName: string;
  fileExtension: string;
  filePath: string;
  fileDirectory: string;
  description?: string;
  fileSize: number;
  contentType?: string;
  version: number;
  revision?: string;
  sequenceNumber: number;
  partNumber?: string;
  isLocked: boolean;
  lockedBy?: string;
  lockedAt?: Date;
  isObsolete: boolean;
  isCompressed: boolean;
  applicationType: ApplicationType;
  itemType: FileVaultItemType;
  productServiceLine: ProductServiceLine;
  drawingSize: DrawingSize;
  drawingName?: string;
  sheet: number;
  totalSheets: number;
  filePrefix?: string;
  drawingSuffix?: string;
  drawingLogId?: number;
  targetId?: number;
  serialNumber?: number;
  documentVersionId?: string;
  partVersionId?: string;
  createdAt: Date;
  createdBy?: string;
  modifiedAt: Date;
  modifiedBy?: string;
  checksum?: string;
  storageLocation?: string;
  metadata?: Record<string, any>;
  validationMessages?: string[];
  fullFileName?: string;
}

export interface FileHistory {
  id: string;
  fileId: string;
  legacyId?: number;
  legacyFileId?: number;
  version: number;
  revision?: string;
  action: FileAction;
  actionDetails?: string;
  notes?: string;
  userId: number;
  userName?: string;
  fileName?: string;
  filePath?: string;
  fileSize?: number;
  checksum?: string;
  timestamp: Date;
  archivedFilePath?: string;
  context?: Record<string, any>;
}

export interface DirectoryNode {
  id: number;
  name: string;
  fullPath: string;
  isExpanded: boolean;
  isSelected: boolean;
  permissions?: FilePermissions;
  children?: DirectoryNode[];
  childrenCount: number;
  files?: FileVaultItem[];
  fileCount: number;
  subdirectoryCount: number;
  lastModified?: Date;
}

export interface FilePermissions {
  canRead: boolean;
  canWrite: boolean;
  canDelete: boolean;
  canWriteFiles: boolean;
  canWriteFilesWithoutApproval: boolean;
  canDeleteFiles: boolean;
  directoryPermissionDisplay?: string;
  filePermissionDisplay?: string;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface FileSearchCriteria {
  searchText?: string;
  legacyId?: number;
  fileName?: string;
  fileExtension?: string;
  directory?: string;
  includeSubdirectories?: boolean;
  applicationType?: ApplicationType;
  productServiceLine?: ProductServiceLine;
  targetId?: number;
  tags?: string[];
  isLocked?: boolean;
  isObsolete?: boolean;
  createdBy?: string;
  modifiedBy?: string;
  createdAfter?: Date;
  createdBefore?: Date;
  modifiedAfter?: Date;
  modifiedBefore?: Date;
  minFileSize?: number;
  maxFileSize?: number;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDescending?: boolean;
}

// Enums matching C# enums

export enum ApplicationType {
  None = 0,
  DesignLog = 1,
  EJDF = 2,
  RollerCone = 3,
  RTA = 4,
  RTS = 5,
  Drawing = 6,
  HolePlan = 7,
  BHA = 11,
  Survey = 12,
  WellLog = 14,
  PostRunDoc = 15,
  ToolStatus = 16,
  WorkOrder = 17,
  JobHistory = 18,
  Cerebro = 19,
  Oculus = 20,
  Proposals = 21,
  CustomerFacing = 22,
  MarketingMaterials = 23,
  ManualOrGuide = 24,
  RCPM = 32,
  FixedCutterPM = 33,
  ToolCheck = 34,
  RCQA = 39,
  SerialNumber = 40,
  JobRecord = 41,
  PartMaster = 42,
  FixedCutterMarketingSheetPhotos = 43
}

export enum FileVaultItemType {
  File = 0,
  Drawing = 1,
  Document = 2,
  Image = 3,
  Video = 4,
  Archive = 5,
  Other = 6
}

export enum ProductServiceLine {
  None = 0,
  RollerCone = 1,
  FixedCutter = 2,
  Fishing = 3
}

// ============ Application Type & Application File Models ============

export interface ApplicationTypeInfo {
  id: number;
  name: string;
  description?: string;
  fileTypeDirectoryId?: number;
  isActive: boolean;
  /** When true, every new ApplicationFile under this type inherits IsGlobalView = true */
  isGlobalViewDefault: boolean;
  createdAt: string;
  modifiedAt: string;
}

export interface ApplicationFileInfo {
  id: number;
  applicationTypeId: number;
  applicationTypeName?: string;
  targetId: number;
  fileId: string;
  fileName?: string;
  deletionFlag: boolean;
  modifiedDate?: string;
  modifiedUserId?: number;
  isGlobalView?: boolean;
  fileTypeDirId?: number;
  createdAt: string;
}

/**
 * Configuration for cross-application-type file visibility.
 * The ownerApplicationTypeId sees all its files.
 * Additional app type IDs only show files where isGlobalView = true.
 */
export interface ApplicationFileVisibilityConfig {
  /** Primary app type - all files are visible */
  ownerApplicationTypeId: number;
  /** Additional app type IDs - only isGlobalView=true files shown */
  additionalApplicationTypeIds?: number[];
  /** Target entity ID (e.g., request number) */
  targetId: number;
}

export enum DrawingSize {
  None = 0,
  A = 1,
  B = 2,
  C = 3,
  D = 4
}

export enum FileAction {
  None = 0,
  Create = 1,
  Read = 2,
  Update = 3,
  Delete = 4,
  CheckOut = 5,
  CheckIn = 6,
  UndoCheckOut = 7,
  Lock = 8,
  Unlock = 9,
  Move = 10,
  Copy = 11,
  Rename = 12,
  Archive = 13,
  Restore = 14,
  Download = 15,
  Upload = 16,
  Share = 17,
  Unshare = 18,
  AddPermission = 19,
  RemovePermission = 20,
  AddTag = 21,
  RemoveTag = 22,
  SetMetadata = 23,
  ClearMetadata = 24,
  Compress = 25,
  Decompress = 26
}

// Upload related models

export interface ChunkUploadInfo {
  fileId: string;
  fileName: string;
  fileSize: number;
  chunkSize: number;
  totalChunks: number;
  uploadedChunks: number;
  status: UploadStatus;
  progress: number;
  startTime: Date;
  lastChunkTime?: Date;
  error?: string;
  errorCode?: string;
  errorDetails?: string[];
}

export interface FileChunk {
  uploadId: string;
  chunkIndex: number;
  totalChunks: number;
  data: Blob;
  fileName: string;
  fileSize: number;
  checksum?: string;
}

export enum UploadStatus {
  Pending = 'pending',
  Uploading = 'uploading',
  Paused = 'paused',
  Completed = 'completed',
  Failed = 'failed',
  Cancelled = 'cancelled'
}

export interface UploadOptions {
  chunkSize?: number; // Default 5MB
  maxConcurrentChunks?: number; // Default 3
  retryAttempts?: number; // Default 3
  retryDelay?: number; // Default 1000ms
  autoStart?: boolean; // Default true
  targetId?: number;
  applicationType?: ApplicationType;
  description?: string;
  metadata?: Record<string, any>;
  /** Sub-PSL name used to resolve fileshare path via FileTypeDirectory (e.g. 'FixedCutter') */
  subPslName?: string;
}

// API Response types

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface UploadInitResponse {
  uploadId: string;
  chunkSize: number;
  totalChunks: number;
}

export interface ChunkUploadResponse {
  uploadId: string;
  chunkIndex: number;
  success: boolean;
  error?: string;
}

export interface UploadCompleteResponse {
  fileId: string;
  file: FileVaultItem;
  success: boolean;
}

// ============ Drawing Number Models ============

export interface PnaPart {
  id: number;
  pnaGenreId: number;
  pnaGenre?: PnaGenre;
  name: string;
  subPsl: number;
  description?: string;
  sapNumberId?: number;
  prefix?: string;
  sequence?: number;
}

export interface PnaGenre {
  id: number;
  name: string;
}

export interface CreateDrawingLogRequest {
  rtaNum?: number;
  pnaPartId?: number;
  description: string;
  createdBy?: number;
  isCreatedInSap?: boolean;
  /** Optional: Part ID (GUID) to auto-create a Document and link it */
  partId?: string;
  /** Optional: Part number to look up the Part if partId is not provided */
  partNumber?: string;
}

export interface UpdateDrawingLogRequest {
  description?: string;
  rtaNum?: number | null;
  clearRta?: boolean;
  isCreatedInSap?: boolean;
}

export interface DrawingNumberResult {
  drawingLogId: number;
  rtaNum?: number;
  pnaPartId?: number;
  prefix?: string;
  drawingNumber: string;
  description: string;
  createDate: Date;
  isCreatedInSap: boolean;
  /** The fv_Document created for this drawing number. Null if no Part was provided. */
  documentId?: string;
  /** Whether the document was linked to a Part. */
  documentLinked: boolean;
  /** Warning message if Part was not found or not provided. */
  warning?: string;
}

// ============ Lifecycle Models ============

export enum LifecycleState {
  InWork = 0,
  InReview = 1,
  Approved = 2,
  Released = 3,
  Superseded = 4,
  Obsolete = 5
}

export interface LifecycleStateInfo {
  entityId: string;
  entityType: string;
  currentState: LifecycleState;
  isReleased: boolean;
  isEditable: boolean;
  isFinal: boolean;
}

export interface LifecycleTransition {
  targetState: LifecycleState;
  targetStateValue: number;
  transitionName: string;
  description: string;
  requiresNotes: boolean;
  requiresApproval: boolean;
}

export interface LifecycleHistoryItem {
  id: string;
  entityId: string;
  entityType: string;
  fromState: LifecycleState;
  toState: LifecycleState;
  transitionedBy: string;
  transitionedAt: Date;
  notes?: string;
  reason?: string;
}

export interface LifecycleTransitionResponse {
  success: boolean;
  entityId: string;
  entityType: string;
  newState: LifecycleState;
}

export interface LifecycleStateMachineEntry {
  state: string;
  stateValue: number;
  isReleased: boolean;
  isEditable: boolean;
  isFinal: boolean;
  allowedTransitions: LifecycleTransition[];
}

// ============ BOM Models ============

export enum PartType {
  Component = 0,
  Assembly = 1,
  RawMaterial = 2,
  Purchased = 3,
  Fabricated = 4,
  Consumable = 5,
  Tool = 6,
  Phantom = 7
}

export enum DocumentType {
  Drawing = 0,
  Specification = 1,
  Manual = 2,
  Procedure = 3,
  TestReport = 4,
  Certificate = 5,
  CADModel = 6,
  Image = 7,
  Other = 99
}

export enum LinkType {
  Primary = 0,
  Reference = 1,
  Specification = 2,
  TestData = 3
}

export interface Part {
  id: string;
  partNumber: string;
  name: string;
  description?: string;
  partType: PartType;
  unitOfMeasure: string;
  lifecycleState: LifecycleState;
  lifecycleStage?: string;
  currentRevision?: string;
  latestVersionId?: string;
  attributes?: Record<string, any>;
  createdAt: Date;
  createdBy: string;
  modifiedAt: Date;
  modifiedBy: string;
  isDeleted: boolean;
}

export interface Document {
  id: string;
  documentNumber: string;
  title: string;
  description?: string;
  documentType: DocumentType;
  lifecycleState: LifecycleState;
  currentRevision?: string;
  latestVersionId?: string;
  attributes?: Record<string, any>;
  createdAt: Date;
  createdBy: string;
  modifiedAt: Date;
  modifiedBy: string;
  isDeleted: boolean;
}

export interface DocumentVersion {
  id: string;
  documentId: string;
  versionNumber: number;
  revision: string;
  state: LifecycleState;
  changeId?: string;
  effectiveFrom?: Date;
  effectiveTo?: Date;
  releaseDate?: Date;
  releasedBy?: string;
  notes?: string;
  attributes?: Record<string, any>;
  createdAt: Date;
  createdBy: string;
  modifiedAt: Date;
  modifiedBy: string;
  isLocked: boolean;
  lockedBy?: string;
  lockedAt?: Date;
}

export interface CreatePartRequest {
  partNumber: string;
  name: string;
  description?: string;
  partType?: PartType;
  unitOfMeasure?: string;
  attributes?: Record<string, any>;
}

export interface CreateDocumentRequest {
  documentNumber: string;
  title: string;
  description?: string;
  documentType?: DocumentType;
  attributes?: Record<string, any>;
}

export interface LinkDocumentRequest {
  documentId: string;
  linkType?: LinkType;
  purpose?: string;
}

export interface BomDocumentLinkRequest {
  documentNumber: string;
  documentVersion?: string;
  documentPart?: string;
  documentType?: string;
  title?: string;
  description?: string;
  bomItemNumber?: string;
  bomItemCategory?: string;
  createdBy: string;
}

export interface LinkDocumentsToPartRequest {
  partNumber: string;
  documents: BomDocumentLinkRequest[];
}

export interface DocumentLinkDetail {
  linkId: string;
  documentId: string;
  documentNumber: string;
  wasCreated: boolean;
  error?: string;
}

export interface LinkDocumentsToPartResponse {
  partId: string;
  partNumber: string;
  partCreated: boolean;
  documentsLinked: number;
  documentsCreated: number;
  errors: string[];
  linkedDocuments: DocumentLinkDetail[];
}

export interface PartVersion {
  id: string;
  partId: string;
  version: number;
  revision: string;
  lifecycleState: LifecycleState;
  description?: string;
  attributes?: Record<string, any>;
  createdAt: Date;
  createdBy: string;
}

export interface PartRelationship {
  id: string;
  parentPartId: string;
  childPartId: string;
  parentVersionId?: string;
  childVersionId?: string;
  quantity: number;
  unitOfMeasure?: string;
  findNumber?: string;
  refDesignator?: string;
  effectiveFrom?: Date;
  effectiveTo?: Date;
  notes?: string;
  sortOrder: number;
  createdAt: Date;
  createdBy: string;
  modifiedAt: Date;
  modifiedBy: string;
  // Navigation properties (populated by API)
  parentPart?: Part;
  childPart?: Part;
  parentVersion?: PartVersion;
  childVersion?: PartVersion;
}

export interface PartDocumentLink {
  id: string;
  partId: string;
  documentId: string;
  linkType: LinkType;
  purpose?: string;
  effectiveFrom?: Date;
  effectiveTo?: Date;
  createdAt: Date;
  createdBy: string;
  part?: Part;
  document?: Document;
}

export interface DocumentUploadResult {
  documentId: string;
  documentVersionId: string;
  drawingNumber: string;
  partNumber: string;
  partId: string;
  partVersionId: string;
  files: UploadedFileInfo[];
}

export interface UploadedFileInfo {
  fileId: string;
  fileName: string;
  size: number;
}

export interface BomNode {
  part: Part;
  version?: PartVersion;
  relationship?: PartRelationship;
  quantity: number;
  extendedQuantity: number;
  level: number;
  findNumber?: string;
  refDesignator?: string;
  children: BomNode[];
  hasMoreChildren: boolean;
}

export interface BomLineItem {
  level: number;
  findNumber?: string;
  partNumber: string;
  partName: string;
  revision?: string;
  quantity: number;
  extendedQuantity: number;
  unitOfMeasure?: string;
  refDesignator?: string;
  notes?: string;
  partId: string;
  partVersionId?: string;
  relationshipId?: string;
}

export enum BomDifferenceType {
  Added = 0,
  Removed = 1,
  QuantityChanged = 2,
  RevisionChanged = 3,
  FindNumberChanged = 4,
  Multiple = 5
}

export interface BomDifference {
  differenceType: BomDifferenceType;
  part: Part;
  previousQuantity?: number;
  newQuantity?: number;
  previousFindNumber?: string;
  newFindNumber?: string;
  previousRevision?: string;
  newRevision?: string;
  description: string;
}

export interface BomQueryOptions {
  maxDepth?: number;
  effectiveDate?: Date;
  includeInactive?: boolean;
  flatten?: boolean;
  calculateExtendedQuantity?: boolean;
}

export interface AddBomChildRequest {
  childPartId: string;
  childVersionId?: string;
  quantity: number;
  unitOfMeasure?: string;
  findNumber?: string;
  refDesignator?: string;
  effectiveFrom?: Date;
  effectiveTo?: Date;
  notes?: string;
  sortOrder?: number;
}

export interface UpdateBomChildRequest {
  childVersionId?: string;
  quantity?: number;
  unitOfMeasure?: string;
  findNumber?: string;
  refDesignator?: string;
  effectiveFrom?: Date;
  effectiveTo?: Date;
  notes?: string;
  sortOrder?: number;
}

export interface BomValidationResult {
  isValid: boolean;
  errors: BomValidationError[];
  warnings: BomValidationWarning[];
}

export interface BomValidationError {
  errorCode: string;
  message: string;
  affectedPartId?: string;
  affectedRelationshipId?: string;
}

export interface BomValidationWarning {
  warningCode: string;
  message: string;
  affectedPartId?: string;
}

// ============ Change Management Models ============

export enum ChangeType {
  ECR = 0,      // Engineering Change Request
  ECO = 1,      // Engineering Change Order
  Deviation = 2,
  ECN = 3       // Engineering Change Notice
}

export enum ChangeRequestType {
  /** Request to modify or replace existing items. Requires affected items before submission. */
  Modification = 0,
  /** Request to create a brand-new part or document. No affected items required at submission. */
  NewCreation = 1,
  /** Request to obsolete / retire existing items. Requires affected items before submission. */
  Obsolescence = 2
}

export enum ChangeState {
  Draft = 0,
  Submitted = 1,
  InReview = 2,
  Approved = 3,
  Rejected = 4,
  InImplementation = 5,
  Implemented = 6,
  Closed = 7,
  Cancelled = 8
}

export enum ChangePriority {
  Low = 0,
  Medium = 1,
  High = 2,
  Critical = 3
}

export enum ChangeItemAction {
  Add = 0,
  Modify = 1,
  Remove = 2,
  Replace = 3
}

export interface Change {
  id: string;
  changeNumber: string;
  changeType: ChangeType;
  /** Purpose of the ECR — controls submission validation and UI behaviour. */
  requestType: ChangeRequestType;
  title: string;
  description?: string;
  justification?: string;
  state: ChangeState;
  priority: ChangePriority;
  raisedBy: string;
  raisedAt: Date;
  assigneeId?: number;
  assigneeName?: string;
  dueDate?: Date;
  effectiveDate?: Date;
  approvedAt?: Date;
  approvedBy?: string;
  closedAt?: Date;
  closedBy?: string;
  parentChangeId?: string;
  parentChangeNumber?: string;
  workflowInstanceId?: string;
  attributes?: Record<string, any>;
  createdAt: Date;
  createdBy: string;
  modifiedAt: Date;
  modifiedBy: string;
  affectedItems?: ChangeItem[];
}

export interface ChangeItem {
  id: string;
  changeId: string;
  entityType: string;
  entityId: string;
  entityNumber?: string;
  entityName?: string;
  action: ChangeItemAction;
  fromRevision?: string;
  toRevision?: string;
  notes?: string;
  sortOrder: number;
  createdAt: Date;
  createdBy: string;
}

export interface ChangeSearchCriteria {
  searchText?: string;
  changeNumber?: string;
  changeType?: ChangeType;
  state?: ChangeState;
  states?: ChangeState[];
  priority?: ChangePriority;
  raisedBy?: string;
  assigneeId?: number;
  raisedAfter?: Date;
  raisedBefore?: Date;
  dueAfter?: Date;
  dueBefore?: Date;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDescending?: boolean;
}

export interface CreateChangeRequest {
  changeType: ChangeType;
  /** Purpose of the request. Use NewCreation for shop-floor requests. */
  requestType?: ChangeRequestType;
  title: string;
  description?: string;
  justification?: string;
  priority?: ChangePriority;
  assigneeId?: number;
  dueDate?: Date;
  effectiveDate?: Date;
  attributes?: Record<string, any>;
  affectedItems?: AddChangeItemRequest[];
}

export interface UpdateChangeRequest {
  title?: string;
  description?: string;
  justification?: string;
  priority?: ChangePriority;
  /** Update the request type (Modification / NewCreation / Obsolescence). */
  requestType?: ChangeRequestType;
  assigneeId?: number;
  dueDate?: Date;
  effectiveDate?: Date;
  attributes?: Record<string, any>;
}

export interface AddChangeItemRequest {
  entityType: string;
  entityId: string;
  /** Human-readable identifier (e.g. material number). Required for cross-system entities. */
  entityNumber?: string;
  /** Display name / description. Required for cross-system entities. */
  entityName?: string;
  action: ChangeItemAction;
  fromRevision?: string;
  toRevision?: string;
  notes?: string;
  sortOrder?: number;
}

export interface UpdateChangeItemRequest {
  action?: ChangeItemAction;
  fromRevision?: string;
  toRevision?: string;
  notes?: string;
  sortOrder?: number;
}

export interface ChangeTransitionResult {
  success: boolean;
  changeId: string;
  newState: ChangeState;
  errorMessage?: string;
  workflowInstanceId?: string;
  /** The updated change object returned by the API after the transition */
  change?: Change;
}

export interface SubmitChangeRequest {
  notes?: string;
  workflowTemplateId?: string;
}

export interface ApproveChangeRequest {
  comments?: string;
}

export interface RejectChangeRequest {
  reason: string;
}

export interface StartImplementationRequest {
  notes?: string;
  workflowTemplateId?: string;
  requiredSignoffs?: string[];
  signoffsParallel?: boolean;
}

export interface ImplementChangeRequest {
  notes?: string;
  effectiveDate?: Date;
}

export interface CloseChangeRequest {
  notes?: string;
}

export interface ConvertToEcoRequest {
  effectiveDate?: Date;
  notes?: string;
}

export interface ChangeDashboardCounts {
  byState: Record<ChangeState, number>;
  total: number;
  overdue: number;
  myAssigned: number;
  myRaised: number;
}

// ============ Notification Models ============

export enum NotificationType {
  WorkflowAssigned = 0,
  ApprovalRequired = 1,
  StateChanged = 2,
  TaskOverdue = 3,
  ChangeSubmitted = 4,
  ChangeApproved = 5,
  ChangeRejected = 6,
  FileCheckedOut = 7,
  FileCheckedIn = 8,
  Mention = 9,
  PermissionGranted = 10,
  PermissionRevoked = 11,
  DailyDigest = 12,
  WeeklyDigest = 13,
  SystemAnnouncement = 14,
  Custom = 99
}

export enum NotificationPriority {
  Low = 0,
  Medium = 1,
  High = 2,
  Critical = 3
}

export enum DigestFrequency {
  Immediate = 0,
  Hourly = 1,
  Daily = 2,
  Weekly = 3,
  None = 4
}

export enum WebhookDeliveryStatus {
  Pending = 0,
  Sending = 1,
  Sent = 2,
  Failed = 3
}

export interface Notification {
  id: string;
  userId: number;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message?: string;
  entityType?: string;
  entityId?: string;
  actionUrl?: string;
  isRead: boolean;
  readAt?: Date;
  isDismissed: boolean;
  dismissedAt?: Date;
  createdAt: Date;
  expiresAt?: Date;
  metadata?: Record<string, any>;
}

export interface NotificationTemplate {
  id: number;
  name: string;
  eventType: string;
  subject: string;
  bodyTemplate: string;
  isHtml: boolean;
  isActive: boolean;
  createdAt: Date;
  modifiedAt: Date;
}

export interface UserNotificationPreference {
  id: number;
  userId: number;
  eventType: string;
  emailEnabled: boolean;
  inAppEnabled: boolean;
  digestFrequency: DigestFrequency;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  createdAt: Date;
  modifiedAt: Date;
}

export interface Webhook {
  id: string;
  name: string;
  description?: string;
  url: string;
  secret?: string;
  events: string[];
  headers?: Record<string, string>;
  isActive: boolean;
  retryCount: number;
  timeoutSeconds: number;
  createdBy: string;
  createdAt: Date;
  modifiedAt: Date;
  lastTriggeredAt?: Date;
  lastSuccessAt?: Date;
  lastFailureAt?: Date;
  failureCount: number;
  successCount: number;
}

export interface WebhookDelivery {
  id: string;
  webhookId: string;
  eventType: string;
  payload: string;
  status: WebhookDeliveryStatus;
  responseCode?: number;
  responseBody?: string;
  error?: string;
  attempts: number;
  createdAt: Date;
  sentAt?: Date;
  durationMs?: number;
}

export interface NotificationSearchCriteria {
  type?: NotificationType;
  priority?: NotificationPriority;
  isRead?: boolean;
  isDismissed?: boolean;
  entityType?: string;
  entityId?: string;
  createdFrom?: Date;
  createdTo?: Date;
  includeExpired?: boolean;
  pageNumber?: number;
  pageSize?: number;
}

export interface NotificationSearchResult {
  items: Notification[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  unreadCount: number;
}

export interface NotificationSummary {
  unreadCount: number;
  urgentCount: number;
  countsByType: Record<NotificationType, number>;
  recentUnread: Notification[];
}

export interface SendNotificationRequest {
  userIds: number[];
  type: NotificationType;
  priority?: NotificationPriority;
  title: string;
  message?: string;
  entityType?: string;
  entityId?: string;
  actionUrl?: string;
  metadata?: Record<string, any>;
  sendEmail?: boolean;
  expiresAt?: Date;
}

export interface MarkNotificationsReadRequest {
  notificationIds?: string[];
}

export interface UpdateNotificationPreferencesRequest {
  preferences: NotificationPreferenceUpdate[];
}

export interface NotificationPreferenceUpdate {
  eventType: string;
  emailEnabled: boolean;
  inAppEnabled: boolean;
  digestFrequency: DigestFrequency;
}

export interface CreateWebhookRequest {
  name: string;
  description?: string;
  url: string;
  secret?: string;
  events: string[];
  headers?: Record<string, string>;
  retryCount?: number;
  timeoutSeconds?: number;
}

export interface UpdateWebhookRequest {
  name?: string;
  description?: string;
  url?: string;
  secret?: string;
  events?: string[];
  headers?: Record<string, string>;
  isActive?: boolean;
  retryCount?: number;
  timeoutSeconds?: number;
}

// ============ Search Models ============

export enum SearchEntityType {
  All = 0,
  Part = 1,
  Document = 2,
  File = 3,
  Change = 4
}

export interface SearchCriteria {
  query: string;
  entityType?: SearchEntityType;
  lifecycleState?: LifecycleState;
  createdAfter?: Date;
  createdBefore?: Date;
  createdBy?: string;
  partType?: string;
  documentType?: string;
  project?: string;
  latestVersionOnly?: boolean;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDescending?: boolean;
}

export interface SearchResultItem {
  entityType: string;
  entityId: string;
  versionId?: string;
  identifier: string;
  name: string;
  description?: string;
  revision?: string;
  state?: string;
  subType?: string;
  createdAt: Date;
  modifiedAt: Date;
  createdBy?: string;
  score: number;
  highlight?: string;
  thumbnailUrl?: string;
}

export interface SearchResults {
  items: SearchResultItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  entityTypeFacets: Record<string, number>;
  stateFacets: Record<string, number>;
  subTypeFacets: Record<string, number>;
  executionTimeMs: number;
}

export interface SearchSuggestion {
  text: string;
  entityType: string;
  entityId?: string;
  category: string;
  matchCount: number;
}

export interface RecentSearch {
  id: string;
  query: string;
  entityType: SearchEntityType;
  searchedAt: Date;
  resultCount: number;
}

export interface SavedSearch {
  id: string;
  name: string;
  query: string;
  criteria: SearchCriteria;
  isShared: boolean;
  createdBy: string;
  createdAt: Date;
}

// ============ Common Directory Models ============

export interface CommonUser {
  id: number;
  displayName: string;
  loginId: string;
  email: string;
  active: boolean;
  roleId: number;
  createdDate: Date;
  createdBy: number;
  modifiedDate?: Date;
  modifiedBy?: number;
  pin?: string;
  isInternal?: boolean;
  oktaId?: string;
}

export interface CommonRole {
  id: number;
  parentId?: number;
  name: string;
  description: string;
  groupId: number;
}

export interface CommonPermission {
  id: number;
  name: string;
  description: string;
  isSapRelated: boolean;
}

export interface CommonUserDetail {
  user: CommonUser;
  role?: CommonRole;
  permissions: CommonPermission[];
}

export interface UpdateUserRoleRequest {
  roleId: number;
}

export interface UpdateUserPermissionsRequest {
  permissionIds: number[];
}

// ============ Workflow Models ============

export enum WorkflowState {
  Pending = 0,
  InProgress = 1,
  Completed = 2,
  Cancelled = 3,
  Failed = 4
}

export enum WorkflowStepState {
  Pending = 0,
  InProgress = 1,
  Approved = 2,
  Rejected = 3,
  Skipped = 4,
  Delegated = 5
}

export enum WorkflowDecision {
  Approve = 0,
  Reject = 1,
  RequestChanges = 2,
  Delegate = 3
}

export interface WorkflowTemplate {
  id: string;
  name: string;
  description?: string;
  entityType: string;
  isActive: boolean;
  steps: WorkflowStepDefinition[];
  createdAt: Date;
  createdBy: string;
  modifiedAt: Date;
  modifiedBy: string;
}

export interface WorkflowStepDefinition {
  stepIndex: number;
  stepName: string;
  description?: string;
  assignedToRole?: string;
  assignedToUserId?: number;
  assignedToUserName?: string;
  dueDays?: number;
  canSkip: boolean;
  requiresComments: boolean;
  allowedDecisions: WorkflowDecision[];
  isOptional?: boolean;
  optionalKey?: string;
  requiredPermissions?: string[];
}

export interface WorkflowInstance {
  id: string;
  templateId: string;
  templateName?: string;
  entityType: string;
  entityId: string;
  entityNumber?: string;
  entityTitle?: string;
  state: WorkflowState;
  currentStepIndex: number;
  startedAt: Date;
  startedBy: string;
  completedAt?: Date;
  attributes?: Record<string, any>;
  template?: WorkflowTemplate;
  steps: WorkflowStep[];
}

export interface WorkflowStep {
  id: string;
  instanceId: string;
  stepIndex: number;
  stepName: string;
  description?: string;
  state: WorkflowStepState;
  assignedTo?: string;
  assignedToRole?: string;
  dueDate?: Date;
  startedAt?: Date;
  completedAt?: Date;
  completedBy?: string;
  decision?: WorkflowDecision;
  comments?: string;
  delegatedTo?: string;
  delegatedAt?: Date;
  canSkip: boolean;
  requiresComments: boolean;
  instance?: WorkflowInstance;
}

export interface WorkflowTask {
  stepId: string;
  instanceId: string;
  templateId: string;
  workflowName: string;
  stepName: string;
  stepIndex: number;
  totalSteps: number;
  entityType: string;
  entityId: string;
  entityNumber?: string;
  entityTitle?: string;
  dueDate?: Date;
  isOverdue: boolean;
  assignedAt?: Date;
  assignedTo?: string;
  assignedToRole?: string;
  canSkip: boolean;
  requiresComments: boolean;
  allowedDecisions: WorkflowDecision[];
}

export interface WorkflowProgress {
  instanceId: string;
  state: WorkflowState;
  totalSteps: number;
  completedSteps: number;
  currentStepIndex: number;
  currentStepName?: string;
  percentComplete: number;
  startedAt: Date;
  estimatedCompletion?: Date;
  steps: WorkflowStepSummary[];
}

export interface WorkflowStepSummary {
  stepIndex: number;
  stepName: string;
  state: WorkflowStepState;
  assignedTo?: string;
  assignedToRole?: string;
  dueDate?: Date;
  completedAt?: Date;
  completedBy?: string;
  decision?: WorkflowDecision;
}

export interface WorkflowStepResult {
  success: boolean;
  newStepState?: WorkflowStepState;
  workflowState?: WorkflowState;
  nextStepIndex?: number;
  errorMessage?: string;
  step?: WorkflowStep;
}

export interface StartWorkflowResult {
  success: boolean;
  workflow?: WorkflowInstance;
  errorMessage?: string;
}

// Workflow Request DTOs

export interface CreateWorkflowTemplateRequest {
  name: string;
  description?: string;
  entityType: string;
  steps: CreateWorkflowStepDefinitionRequest[];
}

export interface CreateWorkflowStepDefinitionRequest {
  stepName: string;
  description?: string;
  assignedToRole?: string;
  assignedToUserId?: number;
  dueDays?: number;
  canSkip?: boolean;
  requiresComments?: boolean;
  allowedDecisions?: WorkflowDecision[];
  isOptional?: boolean;
  optionalKey?: string;
  requiredPermissions?: string[];
}

export interface UpdateWorkflowTemplateRequest {
  name?: string;
  description?: string;
  isActive?: boolean;
  steps?: CreateWorkflowStepDefinitionRequest[];
}

export interface StartWorkflowRequest {
  templateId: string;
  entityType: string;
  entityId: string;
  attributes?: Record<string, any>;
}

export interface ApproveStepRequest {
  comments?: string;
}

export interface RejectStepRequest {
  reason: string;
}

export interface DelegateStepRequest {
  delegateTo: string;
  reason?: string;
}

export interface SkipStepRequest {
  reason?: string;
}

export interface ClaimStepRequest {
}

export interface AddWorkflowStepRequest {
  stepName: string;
  description?: string;
  assignedToRole?: string;
  assignedToUserName?: string;
  dueDays?: number;
  canSkip?: boolean;
  requiresComments?: boolean;
}

export interface RemoveWorkflowStepRequest {
  reason?: string;
}

export interface CancelWorkflowRequest {
  reason: string;
}

export interface WorkflowSearchCriteria {
  templateId?: string;
  entityType?: string;
  entityId?: string;
  state?: WorkflowState;
  startedBy?: string;
  startedFrom?: Date;
  startedTo?: Date;
  pageNumber?: number;
  pageSize?: number;
}

export interface WorkflowTaskSearchCriteria {
  assignedTo?: string;
  assignedToRole?: string;
  state?: WorkflowStepState;
  includeOverdue?: boolean;
  dueBefore?: Date;
  entityType?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface WorkflowDashboardCounts {
  byState: Record<WorkflowState, number>;
  total: number;
  pending: number;
  inProgress: number;
  completed: number;
}
